/** Timezone-aware date formatting using built-in Intl (no extra deps).
 *
 * The studio's preferred timezone is exposed by BrandProvider via `default_timezone`
 * (auto-derived from `default_country_code` on the server, e.g. "+91" → "Asia/Kolkata").
 * Falls back to the browser's local zone if the prop isn't set.
 */

import { formatInTimeZone } from "date-fns-tz";

const DATE_OPTS = { day: "2-digit", month: "short", year: "numeric" };
const TIME_OPTS = { hour: "2-digit", minute: "2-digit", hour12: true };
const DATETIME_OPTS = { ...DATE_OPTS, ...TIME_OPTS };

/** Format an ISO string in the given timezone.
 *
 * IMPORTANT: Mongo + FastAPI's default JSON encoder serialize datetimes WITHOUT a "Z" suffix,
 * even though they're stored in UTC. JS `new Date(...)` parses naive strings as LOCAL time,
 * causing a tz offset bug. We coerce naive strings to UTC by appending "Z".
 */
const _toDate = (input) => {
  if (input instanceof Date) return input;
  if (!input) return null;
  let s = String(input);
  // If the string has no offset and no trailing Z, treat it as UTC.
  if (!/[zZ]$/.test(s) && !/[+\-]\d{2}:?\d{2}$/.test(s)) {
    s = s.endsWith("Z") ? s : `${s}Z`;
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const formatInTz = (iso, tz, opts = DATETIME_OPTS) => {
  if (!iso) return "—";
  const d = _toDate(iso);
  if (!d) return String(iso);
  try {
    return new Intl.DateTimeFormat("en-IN", { timeZone: tz || undefined, ...opts }).format(d);
  } catch (_) {
    return d.toLocaleString();
  }
};

export const formatDate = (iso, tz) => formatInTz(iso, tz, DATE_OPTS);
export const formatTime = (iso, tz) => formatInTz(iso, tz, TIME_OPTS);
export const formatDateTime = (iso, tz) => formatInTz(iso, tz, DATETIME_OPTS);

/**
 * Drop-in replacement for `date-fns format(new Date(iso), pattern)` that
 * honours the studio timezone. Use this anywhere we already had:
 *   format(new Date(iso), "dd MMM yyyy")
 * → fmt(iso, "dd MMM yyyy", default_timezone)
 *
 * If the date is a `Date` (already local) and no tz is given, we fall back to
 * the browser's local rendering via `Intl` to keep behaviour identical.
 */
export const fmt = (input, pattern, tz) => {
  if (!input) return "—";
  const d = _toDate(input);
  if (!d) return String(input);
  try {
    return formatInTimeZone(d, tz || "UTC", pattern);
  } catch (_) {
    // Bad tz → fall back to local
    try {
      return formatInTimeZone(d, Intl.DateTimeFormat().resolvedOptions().timeZone, pattern);
    } catch {
      return d.toString();
    }
  }
};
